import {
	Button,
	EmptyState,
	Modal,
	ModalContent,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@ondit/ui";
import { ModalHeader } from "~/core/ui/dialog";
import { trackGeneration } from "~/modules/analytics/lib/generation-tracking";
/**
 * What the generator would write, before anything is spent.
 *
 * Every other paid button in this panel puts the price on the control. This
 * screen goes one step further and puts the *input* on the control: the facts
 * the page may state, the sources behind them, and — where there is one — the
 * refusal, in the generator's own words, before the click rather than after it.
 *
 * That is possible because the brief is assembled from the database with no
 * model call, which is also what makes "only use the facts in DATA" enforceable
 * rather than aspirational. The commonest refusal is the source floor, and it
 * is the one worth reading early: it is fixed by adding a source to a fact on
 * the Settings screen, not by trying again.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronRight } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { useTopUp } from "~/common/billing/topup";
import { useAccess, whyDisabled } from "~/core/access/index";
import { ApiError, api, type BriefRow } from "~/core/api/index";
import { useI18n, useT } from "~/core/i18n";
import { brand } from "~/core/paths";
import { keys, read } from "~/core/query";
import { Section } from "~/core/ui/page";
import { Async } from "~/core/ui/states";
import { Drill, DrillLink } from "~/modules/analytics/components/detail";
import { type ReadingProps, StageShell } from "~/modules/analytics/components/stage";
import { readingPath } from "~/modules/analytics/lib/loop";

export function BriefsPage({ entityId, filters, setFilters }: ReadingProps) {
	const t = useT();
	const { locale } = useI18n();
	const [busy, setBusy] = useState<string | null>(null);
	const [selected, setSelected] = useState<string | null>(null);

	const queryClient = useQueryClient();
	const state = useQuery({
		queryKey: [
			...keys.entity(entityId),
			"briefs",
			{ range: filters.range, engine: filters.engine, locale: filters.locale, topics: filters.topics },
		],
		queryFn: () => api.briefs(entityId, filters),
		...read,
	});
	const fromGap = useMutation({
		mutationFn: (gapId: string) => api.generateFromGap(gapId),
		onSuccess: () => void queryClient.invalidateQueries({ queryKey: keys.entity(entityId) }),
	});

	async function generate(gapId: string) {
		setBusy(gapId);
		try {
			trackGeneration((await fromGap.mutateAsync(gapId)).job_id);
			toast.success(t("gaps.queued"));
		} catch (e) {
			toast.error(e instanceof ApiError ? (e.detail ?? e.localized(locale)) : String(e), { duration: Infinity });
		} finally {
			setBusy(null);
		}
	}

	return (
		<StageShell entityId={entityId} reading="briefs" lede={t("briefs.lede")} filters={filters} setFilters={setFilters}>
			<Async
				state={state}
				empty={
					<EmptyState
						size="compact"
						title={t("briefs.empty")}
						description={t("briefs.emptyBody")}
						action={<DrillLink to={readingPath(brand(entityId), "gaps")}>{t("empty.goGaps")}</DrillLink>}
					/>
				}
			>
				{(data) =>
					data.briefs.length === 0 ? (
						<EmptyState
							size="compact"
							title={t("briefs.empty")}
							description={t("briefs.emptyBody")}
							action={<DrillLink to={readingPath(brand(entityId), "gaps")}>{t("empty.goGaps")}</DrillLink>}
						/>
					) : (
						<>
							<div className="brief-list">
								{data.briefs.map((row) => (
									<article className="brief-summary" key={row.gap_id}>
										<div>
											<h2>{row.question}</h2>
											<p className="faint">
												{row.engine === "manual" ? t("manual.gapSource") : row.engine} · {row.week} ·{" "}
												{t("briefs.sources", { n: row.sources.length })}
											</p>
											{row.blocked ? <p className="muted">{t(row.blocked.message_key, row.blocked.params)}</p> : null}
										</div>
										<Button variant="default" onClick={() => setSelected(row.gap_id)}>
											{t("briefs.view")}
										</Button>
									</article>
								))}
							</div>
							<Modal
								open={selected !== null}
								onOpenChange={(open) => {
									if (!open) setSelected(null);
								}}
							>
								<ModalContent className="brief-detail" aria-describedby={undefined}>
									<ModalHeader>{t("reading.briefs")}</ModalHeader>
									{data.briefs
										.filter((row) => row.gap_id === selected)
										.map((row) => (
											<Brief
												key={row.gap_id}
												row={row}
												balance={data.balance}
												busy={busy === row.gap_id}
												onGenerate={generate}
											/>
										))}
								</ModalContent>
							</Modal>
							<p className="faint">
								{t("briefs.foot")} <a href={readingPath(`/w/${entityId}`, "gaps")}>{t("reading.gaps")}</a>
							</p>
						</>
					)
				}
			</Async>
		</StageShell>
	);
}

/**
 * One brief: the question, the fact table, and the button.
 *
 * The facts are a table and not a paragraph because that is the shape the
 * fact-checking pass reads them in afterwards. A person looking at a page that
 * came out wrong should be able to look at this screen and see the same rows
 * the checker had.
 */
function Brief({
	row,
	balance,
	busy,
	onGenerate,
}: {
	row: BriefRow;
	balance: number;
	busy: boolean;
	onGenerate: (gapId: string) => void;
}) {
	const t = useT();
	const { editor, why } = useAccess();
	const topUp = useTopUp();
	const affordable = balance > 0;

	return (
		<Section
			title={row.question}
			lede={t(`briefs.recipe.${row.recipe}`)}
			action={
				row.blocked ? (
					// The reason, not a dead button. A control that always fails is
					// a bug report; a sentence saying what is missing is the next
					// thing to do.
					<span className="faint">{t(row.blocked.message_key, row.blocked.params)}</span>
				) : (
					// No price on the control: a page costs what its models charge,
					// which nothing knows until they have answered. The refusals this
					// screen exists for are the ones that can be checked first.
					<Button
						variant="primary"
						type="button"
						disabled={busy || !editor || !affordable}
						title={whyDisabled(editor, why.editor)}
						onClick={() => onGenerate(row.gap_id)}
					>
						{t("gaps.generate")}
						<ChevronRight aria-hidden />
					</Button>
				)
			}
		>
			<p className="faint">
				{row.engine === "manual" ? t("manual.gapSource") : row.engine} · {row.week}
				{row.competitor_domains.length > 0 ? " · " : ""}
				{row.competitor_domains.slice(0, 3).map((domain) => (
					<Drill className="pill" view={{ kind: "rival", label: domain, domain }} key={domain}>
						{domain}
					</Drill>
				))}
			</p>

			{row.facts.length === 0 ? (
				<EmptyState size="compact" title={t("briefs.noFacts")} description={t("briefs.noFactsBody")} />
			) : (
				<div className="table-scroll">
					<Table variant="resource">
						<TableHeader>
							<TableRow>
								<TableHead className="wrap">{t("briefs.fact")}</TableHead>
								<TableHead className="wrap">{t("briefs.value")}</TableHead>
								<TableHead>{t("briefs.source")}</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{row.facts.map((fact) => (
								<TableRow key={fact.key}>
									<TableCell className="wrap mono">{fact.key}</TableCell>
									<TableCell className="wrap">{fact.value}</TableCell>
									<TableCell>
										{fact.source_url ? (
											<a href={fact.source_url} target="_blank" rel="noreferrer nofollow">
												{t("briefs.hasSource")}
											</a>
										) : (
											// The one warning colour on this screen, and it is the
											// reason the screen exists: a fact with no source is a
											// fact the page may not lean on.
											<span className="warn">{t("briefs.noSource")}</span>
										)}
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</div>
			)}

			<p className="faint">
				{t("briefs.sources", { n: row.sources.length })}
				{/* Not enough credit is the one refusal this screen cannot fix by
				    editing a fact, so it gets a way out rather than a sentence. */}
				{row.blocked === null && !affordable ? (
					<>
						{" "}
						{t("gaps.noCredits")}{" "}
						<Button variant="link" type="button" onClick={() => topUp("credits")}>
							{t("topup.open")}
						</Button>
					</>
				) : null}
			</p>
		</Section>
	);
}
